import React from 'react';
import { motion } from 'framer-motion';
import { SearchIcon, ShieldCheckIcon, ClipboardCheckIcon, FileTextIcon, TruckIcon } from 'lucide-react';
export function Process() {
  const steps = [{
    number: '01',
    icon: SearchIcon,
    title: 'Opportunity Identification',
    description: 'We identify procurement opportunities relating to multilateral, government-backed, and African Union (AU)-funded projects across global markets.'
  }, {
    number: '02',
    icon: ShieldCheckIcon,
    title: 'Supplier Sourcing & Verification',
    description: 'Vet manufacturers and exporters for production capability, quality compliance, export capacity, and financial stability.'
  }, {
    number: '03',
    icon: ClipboardCheckIcon,
    title: 'Specification & Pricing Evaluation',
    description: 'Technical and commercial review of supplier proposals against tender requirements, ensuring compliance and competitive pricing.'
  }, {
    number: '04',
    icon: FileTextIcon,
    title: 'Tender Submission & Contract Liaison',
    description: 'Coordinate documentation, facilitate negotiations, and provide ongoing liaison between suppliers and procurement entities.'
  }, {
    number: '05',
    icon: TruckIcon,
    title: 'Logistics & Delivery Coordination',
    description: 'Export documentation, freight coordination, customs clearance, and in-country delivery management.'
  }];
  return <section id="process" className="py-24 px-6 bg-navy-dark" data-id="element-187">
      <div className="max-w-7xl mx-auto" data-id="element-188">
        {/* Section Header */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} className="text-center mb-16" data-id="element-189">
          <div className="flex items-center justify-center gap-3 mb-4" data-id="element-190">
            <div className="w-12 h-px bg-gold" data-id="element-191"></div>
            <span className="text-gold text-xs font-semibold tracking-widest uppercase" data-id="element-192">
              Our Process
            </span>
            <div className="w-12 h-px bg-gold" data-id="element-193"></div>
          </div>
          <h2 className="text-4xl lg:text-5xl font-semibold text-white mb-4" data-id="element-194">
            Proven 5-Step Framework
          </h2>
          <p className="text-slate-400 text-lg max-w-2xl mx-auto" data-id="element-195">
            A systematic approach refined over a decade of institutional
            procurement experience
          </p>
        </motion.div>

        {/* Desktop Timeline */}
        <div className="hidden lg:block relative" data-id="element-196">
          {/* Connection Line */}
          <div className="absolute top-16 left-0 right-0 h-0.5 bg-slate-800" data-id="element-197">
            <motion.div initial={{
            width: 0
          }} whileInView={{
            width: '100%'
          }} viewport={{
            once: true
          }} transition={{
            duration: 1.5,
            delay: 0.3
          }} className="h-full bg-gold" data-id="element-198"></motion.div>
          </div>

          {/* Steps */}
          <div className="grid grid-cols-5 gap-4" data-id="element-199">
            {steps.map((step, i) => <motion.div key={step.number} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: i * 0.2
          }} className="relative" data-id="element-200">
                {/* Circle */}
                <div className="relative z-10 w-16 h-16 bg-navy-light border-4 border-gold rounded-full flex items-center justify-center mx-auto mb-6" data-id="element-201">
                  <step.icon size={24} className="text-gold" data-id="element-202" />
                </div>

                {/* Content */}
                <div className="text-center" data-id="element-203">
                  <div className="text-gold text-sm font-bold mb-2" data-id="element-204">
                    {step.number}
                  </div>
                  <h3 className="text-white font-semibold mb-2" data-id="element-205">
                    {step.title}
                  </h3>
                  <p className="text-slate-400 text-sm leading-relaxed" data-id="element-206">
                    {step.description}
                  </p>
                </div>
              </motion.div>)}
          </div>
        </div>

        {/* Mobile Timeline */}
        <div className="lg:hidden space-y-8" data-id="element-207">
          {steps.map((step, i) => <motion.div key={step.number} initial={{
          opacity: 0,
          x: -20
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          delay: i * 0.1
        }} className="flex gap-4" data-id="element-208">
              <div className="flex flex-col items-center" data-id="element-209">
                <div className="w-12 h-12 bg-navy-light border-2 border-gold rounded-full flex items-center justify-center flex-shrink-0" data-id="element-210">
                  <step.icon size={20} className="text-gold" data-id="element-211" />
                </div>
                {i < steps.length - 1 && <div className="w-0.5 h-full bg-slate-800 mt-2" data-id="element-212"></div>}
              </div>
              <div className="pb-8" data-id="element-213">
                <div className="text-gold text-sm font-bold mb-1" data-id="element-214">
                  {step.number}
                </div>
                <h3 className="text-white font-semibold mb-2" data-id="element-215">{step.title}</h3>
                <p className="text-slate-400 text-sm leading-relaxed" data-id="element-216">
                  {step.description}
                </p>
              </div>
            </motion.div>)}
        </div>
      </div>
    </section>;
}